import * as Sentry from '@sentry/react';

// Session Replay recording is PROD-only per Carlos (9/17): dev/QA/localhost
// stay off regardless of auth state. Shared with index.tsx's sample-rate
// config so there is one flag for the policy.
export const isProdEnv = process.env.REACT_APP_ENV === 'prd';

// Tracks a stop-then-start cycle in progress, so concurrent callers (whoIam
// runs repeatedly) join the one in flight instead of each starting their own.
// Cleared when that cycle finishes, whether it completed or was cancelled.
let pendingStart: Promise<void> | null = null;

// Bumped by stopAuthenticatedReplay so a start already in the gap between
// stop() and start() (see below) can tell it was cancelled and must not
// resurrect replay after a logout.
let cancelToken = 0;

/**
 * Force-starts Replay so authenticated sessions record at 100% instead of
 * the 10% anonymous baseline (per Carlos, 9/21). No-op outside prod. Call
 * alongside every Sentry.setUser(...) at login/sso/whoIam. Never throws --
 * none of its callers in auth.service.ts await it.
 *
 * replay.start() alone is NOT enough: with replaysOnErrorSampleRate > 0 every
 * page load already begins recording in "buffer" mode (last 60s, sent only
 * on error), and start() treats any already-enabled recording -- buffer
 * included -- as "in progress" and silently no-ops (verified against a real
 * prod build: recordingMode stayed "buffer" through login). stop() first
 * clears that buffered session so start() actually promotes to "session"
 * mode; stop() on an unflushed buffer doesn't send it, so this doesn't leak
 * the pre-login buffer.
 *
 * stop() and start() can't run back to back, though: stop() destroys the
 * compression worker, which rejects every write still in flight. The SDK's
 * add-event error handler only ignores those rejections while
 * !replay.isEnabled() -- if start() has already run by the time they're
 * handled, the SDK logs them as internal_sdk_error against the NEW session
 * and discards it. Awaiting one macrotask after stop() lets those rejections
 * land while replay is off, before start() turns it back on.
 */
export const startAuthenticatedReplay = (): void => {
    if (!isProdEnv || pendingStart) {
        return;
    }

    const replay = Sentry.getReplay();
    if (!replay || replay.getRecordingMode() === 'session') {
        return;
    }

    const myToken = ++cancelToken;
    // Captured so `finally` only clears pendingStart if it's still THIS
    // cycle -- without this, a cycle cancelled by a logout-then-login inside
    // its own gap would clear the newer cycle's pendingStart out from under
    // it when it finally settles (harmless -- a third call just restarts and
    // the stale cycle bails on its token -- but not exact).
    // Definite-assignment: assigned synchronously below, before the closure's
    // first `await` ever yields back to it -- TS can't see that, but the
    // runtime guarantee holds.
    let cycle!: Promise<void>;
    cycle = (async () => {
        try {
            await replay.stop();
            // Yield one macrotask so the compression worker's now-rejected
            // in-flight writes are handled while replay is off (see above).
            await new Promise<void>((resolve) => {
                setTimeout(resolve, 0);
            });

            // A logout during the gap must win -- don't resurrect replay
            // after stopAuthenticatedReplay() has already run.
            if (myToken !== cancelToken) {
                return;
            }

            replay.start();
        } catch {
            // Never throw past here -- see the function doc.
        } finally {
            if (pendingStart === cycle) {
                pendingStart = null;
            }
        }
    })();
    pendingStart = cycle;
};

/**
 * Stops a force-started replay on logout. Call alongside every
 * Sentry.setUser(null). Cancels a startAuthenticatedReplay() still in its
 * stop/start gap so it can't resurrect replay after this runs.
 */
export const stopAuthenticatedReplay = () => {
    if (isProdEnv) {
        cancelToken += 1;
        pendingStart = null;
        Sentry.getReplay()?.stop();
    }
};
